import { Box, Typography } from "@mui/material";
import { MuiTelInput, MuiTelInputInfo } from "mui-tel-input";
import { FormField, FormSelect } from "./FormControl";
import { BookingData } from "../../../../../types/bookings";

interface Props {
  bookingData: BookingData;
  setBookingData: React.Dispatch<React.SetStateAction<BookingData>>;
}

export function ClientInformation({ bookingData, setBookingData }: Props) {
  const set = (field: keyof BookingData) => (v: string) =>
    setBookingData((prev) => ({ ...prev, [field]: v }));

  const handlePhoneChange = (value: string, _info: MuiTelInputInfo) => {
    void _info;
    setBookingData((prev) => ({ ...prev, phone: value }));
  };

  return (
    <Box>
      <Typography variant="h5" fontWeight={600} mb={3}>
        Client Information
      </Typography>

      {/* ── Title / First Name / Surname ─────────────────────────────── */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "auto 1fr 1fr" },
          gap: 2,
        }}
      >
        <Box sx={{ mt: "-4px" }}>
          <FormSelect
            label="Title"
            value={bookingData.client_salutation ?? ""}
            onChange={set("client_salutation")}
            options={["Mr", "Mrs", "Ms", "Dr"]}
            minWidth={85}
          />
        </Box>
        <FormField
          label="First Name"
          required
          value={bookingData.first_name ?? ""}
          onChange={set("first_name")}
        />
        <FormField
          label="Surname"
          required
          value={bookingData.surname ?? ""}
          onChange={set("surname")}
        />
      </Box>

      {/* ── Phone / Email — same width as name fields, space on right ── */}
      {/* ── Phone / Email ────────────────────────────────────────────── */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          gap: 2,
          mt: 2,
          maxWidth: { sm: "calc(((100% - 85px - 16px) / 2) * 2 + 8px)" }, // matches two name columns
        }}
      >
        <MuiTelInput
          size="small"
          label="Phone"
          value={bookingData.phone ?? ""}
          onChange={handlePhoneChange}
          defaultCountry="ZW"
          variant="outlined"
          sx={{
            flex: 1,
            mt: 2.2,
            "& .MuiOutlinedInput-root": {
              height: "31px",
              fontSize: "0.875rem",
            },
            "& .MuiInputLabel-root": {
              fontSize: "0.875rem",
              top: "-4px",
              "&.MuiInputLabel-shrink": {
                top: 0,
              },
            },
            "& .MuiInputBase-input": {
              padding: "0 8px",
              height: "33px",
            },
          }}
        />

        <FormField
          label="Email"
          type="email"
          value={bookingData.email ?? ""}
          onChange={set("email")}
          sx={{ flex: 1 }}
        />
      </Box>

      {/* ── Notes — full width ───────────────────────────────────────── */}
      <Box sx={{ mt: 2 }}>
        <FormField
          label="Notes"
          value={bookingData.notes ?? ""}
          onChange={set("notes")}
          multiline
          rows={4}
          fullWidth
        />
      </Box>
    </Box>
  );
}
